import { useEffect, useState, useCallback, useRef } from 'react'
import type { RealtimeChannel } from '@supabase/supabase-js'
import * as Sentry from '@sentry/react'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import type { Notification } from '@/types/database'

// ─── Return type ──────────────────────────────────────────────────────────────

interface UseNotificationsReturn {
  notifications: Notification[]
  unreadCount: number
  markAsRead: (id: string) => Promise<void>
  markAllAsRead: () => Promise<void>
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Fetches the current user's notifications and subscribes to new ones via
 * Supabase Realtime.
 *
 * On mount:
 *  1. Loads all existing notifications from the DB (most-recent first).
 *  2. Opens a Realtime channel filtered to `user_id = current_user.id` so
 *     new notifications appear immediately without polling.
 *
 * On unmount: unsubscribes and cleans up the channel.
 */
function useNotifications(): UseNotificationsReturn {
  const { user } = useAuth()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const channelRef = useRef<RealtimeChannel | null>(null)
  // Each hook instance gets a unique ID so concurrent instances (dropdown + page)
  // don't share a channel name and cause the "cannot add callbacks after subscribe" error.
  const instanceId = useRef(Math.random().toString(36).slice(2, 8))

  // ── Computed unread count ──────────────────────────────────────────────────

  const unreadCount = notifications.filter((n) => n.read_at === null).length

  // ── Initial fetch ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (!user?.id) return

    let mounted = true

    async function loadNotifications() {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false })
        .limit(50)

      if (!mounted) return
      if (error) {
        Sentry.captureException(error, { tags: { source: 'useNotifications.fetch' } })
        if (import.meta.env.DEV) {
          console.error('[useNotifications] fetch error:', error.message)
        }
        return
      }

      setNotifications((data ?? []) as Notification[])
    }

    loadNotifications()

    return () => {
      mounted = false
    }
  }, [user?.id])

  // ── Real-time subscription ─────────────────────────────────────────────────

  useEffect(() => {
    if (!user?.id) return

    const channelName = `notifications:user_id=eq.${user.id}:${instanceId.current}`

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const newNotification = payload.new as Notification
          setNotifications((prev) => {
            const alreadyExists = prev.some((n) => n.id === newNotification.id)
            if (alreadyExists) return prev
            // Toast automático para nova notificação
            toast(newNotification.title ?? 'Nova notificação', {
              description: newNotification.body ?? undefined,
              duration: 5000,
            })
            return [newNotification, ...prev]
          })
        },
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const updated = payload.new as Notification
          setNotifications((prev) =>
            prev.map((n) => (n.id === updated.id ? updated : n)),
          )
        },
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR') {
          Sentry.captureMessage(`useNotifications channel error on ${channelName}`, 'error')
          if (import.meta.env.DEV) {
            console.error(
              `[useNotifications] channel error on ${channelName}`,
            )
          }
        }
      })

    channelRef.current = channel

    return () => {
      supabase.removeChannel(channel)
      channelRef.current = null
    }
  }, [user?.id])

  // ── markAsRead ─────────────────────────────────────────────────────────────

  const markAsRead = useCallback(async (id: string) => {
    const now = new Date().toISOString()

    // Optimistic update
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read_at: now } : n)),
    )

    const { error } = await supabase
      .from('notifications')
      .update({ read_at: now })
      .eq('id', id)

    if (error) {
      // Roll back optimistic update on failure
      Sentry.captureException(error, { tags: { source: 'useNotifications.markAsRead' } })
      if (import.meta.env.DEV) {
        console.error('[useNotifications] markAsRead error:', error.message)
      }
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read_at: null } : n)),
      )
      throw new Error(error.message)
    }
  }, [])

  // ── markAllAsRead ──────────────────────────────────────────────────────────

  const markAllAsRead = useCallback(async () => {
    if (!user?.id) return

    const now = new Date().toISOString()

    // Optimistic update
    setNotifications((prev) => prev.map((n) => ({ ...n, read_at: now })))

    const { error } = await supabase
      .from('notifications')
      .update({ read_at: now })
      .eq('user_id', user.id)
      .is('read_at', null)

    if (error) {
      // Roll back — re-fetch rather than trying to reconstruct previous state
      Sentry.captureException(error, { tags: { source: 'useNotifications.markAllAsRead' } })
      if (import.meta.env.DEV) {
        console.error('[useNotifications] markAllAsRead error:', error.message)
      }

      const { data } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50)

      if (data) setNotifications(data as Notification[])
      throw new Error(error.message)
    }
  }, [user?.id])

  return { notifications, unreadCount, markAsRead, markAllAsRead }
}

export { useNotifications }
